import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { TownEyeLogo } from "@/components/TownEyeLogo";
import { Avatar } from "@/components/AppHeader";
import api, { formatApiError } from "@/lib/api";
import { Loader2, Camera, Check, X, Sparkles, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { CitySelect } from "@/components/CitySelect";

const STEPS = ["welcome", "photo", "username", "city", "bio"];

export default function OnboardingPage() {
  const { user, refreshMe } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState("welcome");
  const [photo, setPhoto] = useState(user?.avatar_url || null);
  const [username, setUsername] = useState(user?.username || "");
  const [usernameStatus, setUsernameStatus] = useState(null); // null | "checking" | "available" | "taken"
  const [city, setCity] = useState(user?.city_id || null);
  const [bio, setBio] = useState(user?.bio || "");
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (user?.onboarded) navigate("/dashboard", { replace: true });
  }, [user, navigate]);

  // Debounced username check
  useEffect(() => {
    let cancelled = false;
    const t = setTimeout(async () => {
      if (cancelled) return;
      if (!username) { setUsernameStatus(null); return; }
      const valid = /^[a-z0-9_]{3,20}$/.test(username);
      if (!valid) { setUsernameStatus("invalid"); return; }
      setUsernameStatus("checking");
      try {
        const { data } = await api.get(`/users/check-username`, { params: { username } });
        if (!cancelled) setUsernameStatus(data.available ? "available" : "taken");
      } catch { if (!cancelled) setUsernameStatus(null); }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
  }, [username]);

  const stepIndex = STEPS.indexOf(step);
  const progress = ((stepIndex + 1) / STEPS.length) * 100;

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5MB photo"); return; }
    const fd = new FormData(); fd.append("file", file);
    setUploading(true);
    try {
      const { data } = await api.post("/upload/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(data.url);
      await api.patch("/users/me", { avatar_url: data.url });
      await refreshMe();
      toast.success("Photo uploaded");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); }
  };

  const saveUsername = async () => {
    if (!username) return next();
    if (usernameStatus !== "available") {
      toast.error(usernameStatus === "taken" ? "Username is taken" : "Choose a valid username");
      return;
    }
    setBusy(true);
    try {
      await api.patch("/users/me", { username });
      await refreshMe();
      next();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const saveCity = async () => {
    if (!city) return next();
    setBusy(true);
    try {
      await api.patch("/users/me", { city_id: city });
      await refreshMe();
      next();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const saveBio = async () => {
    setBusy(true);
    try {
      if (bio.trim()) await api.patch("/users/me", { bio: bio.trim() });
      await api.post("/users/complete-onboarding");
      await refreshMe();
      toast.success("You're all set!");
      navigate("/dashboard");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const finishNow = async () => {
    setBusy(true);
    try {
      await api.post("/users/complete-onboarding");
      await refreshMe();
      navigate("/dashboard");
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const next = () => {
    const i = STEPS.indexOf(step);
    if (i < STEPS.length - 1) setStep(STEPS[i + 1]);
    else finishNow();
  };
  const skip = next;

  return (
    <div className="min-h-screen flex flex-col" style={{ background: "var(--te-bg)" }}>
      <header className="px-4 md:px-8 py-5 flex items-center justify-between">
        <TownEyeLogo size={26} />
        {step !== "welcome" && (
          <button onClick={finishNow} disabled={busy} className="text-sm text-[var(--te-text-secondary)] hover:text-[var(--te-text)] font-medium" data-testid="onboarding-finish-now">
            Finish later
          </button>
        )}
      </header>

      {/* Progress bar */}
      {step !== "welcome" && (
        <div className="max-w-md mx-auto w-full px-4 md:px-0">
          <div className="h-1 bg-[var(--te-input-bg)] rounded-full overflow-hidden">
            <div className="h-full bg-[var(--te-primary)] transition-all duration-300" style={{ width: `${progress}%` }} />
          </div>
          <div className="mt-2 text-xs text-[var(--te-text-muted)] text-center">Step {stepIndex} of {STEPS.length - 1}</div>
        </div>
      )}

      <main className="flex-1 flex items-center justify-center px-4 md:px-8 py-8">
        <div className="w-full max-w-md te-fade-up">
          {step === "welcome" && (
            <div className="bg-white rounded-3xl border border-[var(--te-divider)] p-8 sm:p-10 text-center" style={{ boxShadow: "0 10px 40px -10px rgba(28, 33, 31, 0.08)" }}>
              <div className="w-16 h-16 mx-auto rounded-2xl bg-[var(--te-primary)] flex items-center justify-center mb-5">
                <Sparkles size={28} className="text-white" />
              </div>
              <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-[var(--te-text)]">
                {(() => {
                  const first = user?.name?.split(" ")[0] || "";
                  const showName = first && first.toLowerCase() !== "towneye";
                  return showName ? `Welcome to TownEye, ${first}!` : "Welcome to TownEye!";
                })()}
              </h1>
              <p className="text-[var(--te-text-secondary)] mt-3 leading-relaxed">
                Let&apos;s set up your profile so neighbors recognize you. Takes about 30 seconds — you can skip any step.
              </p>
              <button onClick={() => setStep("photo")} className="te-btn-primary w-full mt-7 py-3.5 flex items-center justify-center gap-2" data-testid="onboarding-start-btn">
                Let&apos;s get started <ArrowRight size={16} />
              </button>
              <button onClick={finishNow} disabled={busy} className="mt-3 text-sm text-[var(--te-text-secondary)] hover:text-[var(--te-text)] font-medium" data-testid="onboarding-skip-all">
                Skip — take me to the app
              </button>
            </div>
          )}

          {step === "photo" && (
            <StepCard
              title="Add a profile photo"
              subtitle="A friendly face helps neighbors recognize you in the feed."
            >
              <div className="flex flex-col items-center gap-5">
                <div className="relative">
                  {photo ? (
                    <img src={photo} alt="avatar" className="w-32 h-32 rounded-full object-cover border-4 border-white shadow-lg" />
                  ) : (
                    <Avatar user={{ ...user, avatar_url: null }} size={128} />
                  )}
                  {photo && (
                    <button
                      onClick={async () => {
                        setPhoto(null);
                        try { await api.patch("/users/me", { avatar_url: null }); await refreshMe(); } catch { /* ignore */ }
                      }}
                      className="absolute -top-2 -right-2 bg-white rounded-full p-2 border border-[var(--te-divider)] shadow"
                      data-testid="onboarding-remove-photo"
                      aria-label="Remove photo"
                    >
                      <X size={14} />
                    </button>
                  )}
                </div>
                <label className="te-btn-secondary inline-flex items-center gap-2 cursor-pointer text-sm" data-testid="onboarding-upload-photo">
                  {uploading ? <Loader2 size={14} className="animate-spin" /> : <Camera size={14} />}
                  {uploading ? "Uploading…" : photo ? "Change photo" : "Upload photo"}
                  <input type="file" accept="image/*" className="hidden" onChange={onPhotoChange} />
                </label>
              </div>
              <StepActions onSkip={skip} onNext={next} nextLabel="Continue" busy={busy} testIdPrefix="onboarding-photo" />
            </StepCard>
          )}

          {step === "username" && (
            <StepCard
              title="Pick a username"
              subtitle="Your unique @ across TownEye. 3-20 chars, letters, numbers, and underscores."
            >
              <div>
                <label className="te-label">Username</label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--te-text-muted)]">@</span>
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                    placeholder="alex_morgan"
                    className="te-input pl-8"
                    maxLength={20}
                    data-testid="onboarding-username-input"
                  />
                  {username && usernameStatus === "checking" && (
                    <Loader2 size={16} className="absolute right-4 top-1/2 -translate-y-1/2 animate-spin text-[var(--te-text-muted)]" />
                  )}
                  {username && usernameStatus === "available" && (
                    <Check size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#10B981]" />
                  )}
                  {username && (usernameStatus === "taken" || usernameStatus === "invalid") && (
                    <X size={16} className="absolute right-4 top-1/2 -translate-y-1/2 text-[#DC2626]" />
                  )}
                </div>
                <p className="mt-1.5 text-xs" data-testid="onboarding-username-status">
                  {!username && <span className="text-[var(--te-text-muted)]">Pick something memorable.</span>}
                  {username && usernameStatus === "checking" && <span className="text-[var(--te-text-muted)]">Checking…</span>}
                  {username && usernameStatus === "available" && <span className="text-[#10B981]">@{username} is available ✨</span>}
                  {username && usernameStatus === "taken" && <span className="text-[#DC2626]">That username is taken.</span>}
                  {username && usernameStatus === "invalid" && <span className="text-[#DC2626]">3-20 chars: a-z, 0-9, _</span>}
                </p>
              </div>
              <StepActions
                onSkip={skip}
                onNext={saveUsername}
                nextLabel="Continue"
                nextDisabled={!!username && usernameStatus !== "available"}
                busy={busy}
                testIdPrefix="onboarding-username"
              />
            </StepCard>
          )}

          {step === "city" && (
            <StepCard
              title="Where do you live?"
              subtitle="We use this to show you reports and posts from your area."
            >
              <div>
                <label className="te-label">City or locality</label>
                <CitySelect
                  value={city}
                  onChange={(id) => setCity(id)}
                  placeholder="Search city in Hebrew, Arabic, or English…"
                  testId="onboarding-city-select"
                />
                <p className="text-xs text-[var(--te-text-muted)] mt-2">Tap &quot;Use my current location&quot; to auto-detect your nearest city.</p>
              </div>
              <StepActions onSkip={skip} onNext={saveCity} nextLabel="Continue" busy={busy} testIdPrefix="onboarding-city" />
            </StepCard>
          )}

          {step === "bio" && (
            <StepCard
              title="A short bio (optional)"
              subtitle="Say hi to your neighbors in 160 characters or less."
            >
              <div>
                <label className="te-label">Bio</label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value.slice(0, 160))}
                  placeholder="Local resident, dog walker, coffee enthusiast."
                  className="te-input min-h-[100px] resize-y"
                  data-testid="onboarding-bio-input"
                />
                <div className="text-xs text-[var(--te-text-muted)] mt-1 text-right">{bio.length}/160</div>
              </div>
              <StepActions onSkip={finishNow} onNext={saveBio} nextLabel="Finish setup" busy={busy} testIdPrefix="onboarding-bio" />
            </StepCard>
          )}
        </div>
      </main>
    </div>
  );
}

function StepCard({ title, subtitle, children }) {
  return (
    <div className="bg-white rounded-3xl border border-[var(--te-divider)] p-7 sm:p-9" style={{ boxShadow: "0 10px 40px -10px rgba(28, 33, 31, 0.08)" }}>
      <h2 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-[var(--te-text)]">{title}</h2>
      <p className="text-sm text-[var(--te-text-secondary)] mt-1.5 leading-relaxed">{subtitle}</p>
      <div className="mt-6 space-y-5">{children}</div>
    </div>
  );
}

function StepActions({ onSkip, onNext, nextLabel, nextDisabled, busy, testIdPrefix }) {
  return (
    <div className="flex items-center gap-3 pt-2">
      <button
        type="button"
        onClick={onSkip}
        disabled={busy}
        className="te-btn-secondary text-sm py-2.5 px-4"
        data-testid={`${testIdPrefix}-skip-btn`}
      >
        Skip
      </button>
      <button
        type="button"
        onClick={onNext}
        disabled={busy || nextDisabled}
        className="te-btn-primary text-sm py-2.5 px-5 flex items-center gap-2 flex-1 justify-center disabled:opacity-50 disabled:cursor-not-allowed"
        data-testid={`${testIdPrefix}-next-btn`}
      >
        {busy ? <><Loader2 size={14} className="animate-spin" /> Saving…</> : <>{nextLabel} <ArrowRight size={14} /></>}
      </button>
    </div>
  );
}
